import { useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Phone, RefreshCw, Loader2, Users, Clock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';
import type { Pulse } from '@shared/schema';

// Structural subset of `Pulse` (from shared/schema) that this card actually
// reads. Keeping it loose lets callers pass either the full DB pulse or a
// trimmed timeline projection without an unsafe cast.
export interface CallPulseCardPulse {
  id: number;
  projectId: string;
  pulseDescription?: string | null;
  htmlContent?: string | null;
  callSessionId?: string | null;
  dateAdded?: string | Date | null;
  metadata?: Record<string, unknown> | null;
}

// Compile-time guard: `Pulse` from shared/schema must be assignable to the
// card's expected prop shape. If the schema drifts away from this contract,
// TypeScript will fail the build right here — not at the call site.
type _PulseCompat = Pulse extends CallPulseCardPulse ? true : never;

interface Props {
  pulse: CallPulseCardPulse;
  className?: string;
}

function formatDurationDe(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDateDe(d?: string | Date | null): string {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Phase E.2 (#192) — frontend chrome for category='call' pulses.
 * Renders header (call icon, date, duration, participant count, admin
 * regenerate button), the AI-rendered HTML body via dangerouslySetInnerHTML,
 * and a lightbox dialog for snapshot thumbnails. The HTML body is produced
 * server-side by `renderPulseHtml` in `server/services/call-pulse.service.ts`
 * — never trust untrusted input here, but the server is the only writer.
 */
export function CallPulseCard({ pulse, className }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState<string>('');

  const meta: Record<string, unknown> = pulse.metadata ?? {};
  const rawDuration = meta.durationSeconds;
  const durationSeconds: number | null = typeof rawDuration === 'number' ? rawDuration : null;
  const rawParticipantIds = meta.participantUserIds;
  const participantIds: number[] = Array.isArray(rawParticipantIds)
    ? rawParticipantIds.filter((v): v is number => typeof v === 'number')
    : [];
  const rawNames = meta.participantNames;
  const participantNames: Record<string, string> =
    rawNames && typeof rawNames === 'object' && !Array.isArray(rawNames)
      ? Object.fromEntries(
          Object.entries(rawNames as Record<string, unknown>).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string',
          ),
        )
      : {};
  const aiSummaryFailed = meta.ai_summary_status === 'failed';
  // Task #17 — placeholder states: 'pending' while the transcription
  // pipeline is still running, 'failed' when the sweep gave up. A failed
  // placeholder has no htmlContent, so it needs a client-side banner too.
  const transcriptionPending = meta.transcription_status === 'pending';
  const transcriptionFailedNoContent =
    meta.transcription_status === 'failed' && !pulse.htmlContent;

  function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
  }

  const regenerate = useMutation({
    mutationFn: async () => {
      if (!pulse.callSessionId) throw new Error('no_call_session');
      const res = await apiRequest('POST', `/api/calls/${pulse.callSessionId}/_internal/regenerate-pulse`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `regenerate_failed_${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Pulse regenerated', description: 'The call pulse was updated.' });
      // Phase E.2 (#192): regenerate rewrites the pulse row(s) for every
      // project this call was attributed to. Invalidate every query scope
      // that displays pulse content so timeline, list, and dialog views
      // all refresh — not just the currently-open card.
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulse.id, 'details'] });
      queryClient.invalidateQueries({ queryKey: ['pulse-workbench', pulse.id] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', pulse.projectId, 'pulses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', pulse.projectId] });
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Regeneration failed',
        description: message,
        variant: 'destructive',
      });
    },
  });

  // Event delegation: any click on an <img.call-pulse-snapshot__img> inside
  // the rendered HTML opens the lightbox. We bind once on the container so
  // re-renders of the inner HTML (regenerate) don't leak listeners.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName !== 'IMG') return;
      if (!target.classList.contains('call-pulse-snapshot__img')) return;
      const src = (target as HTMLImageElement).src;
      const alt = (target as HTMLImageElement).alt;
      if (src) {
        setLightboxSrc(src);
        setLightboxAlt(alt);
      }
    };
    node.addEventListener('click', onClick);
    return () => node.removeEventListener('click', onClick);
  }, [pulse.htmlContent]);

  const dateLabel = useMemo(() => formatDateDe(pulse.dateAdded ?? null), [pulse.dateAdded]);

  return (
    <div
      className={cn(
        'call-pulse-card border border-gray-600 rounded-md bg-gray-800 overflow-hidden',
        className,
      )}
      data-testid="call-pulse-card"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-600 bg-gray-750">
        <div className="flex items-center gap-2 min-w-0">
          <Phone className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-gray-100 truncate">
            {pulse.pulseDescription || 'Call'}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
          <span>{dateLabel}</span>
          {durationSeconds != null && (
            <span className="flex items-center gap-1" title="Duration">
              <Clock className="w-3 h-3" />
              {formatDurationDe(durationSeconds)}
            </span>
          )}
          {participantIds.length > 0 && (
            <span className="flex items-center gap-1" title="Participants" data-testid="call-pulse-participant-count">
              <Users className="w-3 h-3" />
              {participantIds.length}
            </span>
          )}
          {isAdmin && pulse.callSessionId && (
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                regenerate.mutate();
              }}
              disabled={regenerate.isPending}
              title="Regenerate pulse"
              data-testid="button-regenerate-call-pulse"
            >
              {regenerate.isPending ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              Regenerate
            </Button>
          )}
        </div>
      </div>

      {participantIds.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-700 flex flex-wrap gap-1.5" data-testid="call-pulse-participant-chips">
          {participantIds.map((uid) => {
            const name = participantNames[String(uid)] || `Speaker ${uid}`;
            return (
              <span
                key={uid}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-700/60 border border-gray-600 text-xs text-gray-200"
                title={name}
                data-testid={`call-pulse-participant-${uid}`}
              >
                <span className="w-4 h-4 rounded-full bg-emerald-600/40 text-emerald-100 text-[10px] font-semibold flex items-center justify-center">
                  {initialsOf(name)}
                </span>
                <span className="truncate max-w-[140px]">{name}</span>
              </span>
            );
          })}
        </div>
      )}

      {aiSummaryFailed && (
        <div
          className="px-3 py-2 text-xs border-b border-yellow-800/40"
          style={{ background: 'rgba(212, 167, 44, 0.12)', color: '#facc15' }}
          data-testid="call-pulse-ai-failed-banner"
        >
          AI summary failed — check the transcript below.
        </div>
      )}

      {transcriptionPending && (
        <div
          className="px-4 py-3 flex items-center gap-2 text-sm text-emerald-300 animate-pulse"
          data-testid="call-pulse-transcription-pending"
        >
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          Transcription in progress… The summary will appear here once it's ready.
        </div>
      )}

      {transcriptionFailedNoContent && (
        <div
          className="px-4 py-3 text-sm"
          style={{ background: 'rgba(220, 38, 38, 0.10)', color: '#fca5a5' }}
          data-testid="call-pulse-transcription-failed"
        >
          Transcription failed — no transcript could be created for this call.
        </div>
      )}

      {!transcriptionPending && !transcriptionFailedNoContent && <div
        ref={containerRef}
        className="prose prose-invert max-w-none prose-sm p-4 pulse-rich-content call-pulse-card__body"
        // Server is the only writer of pulse.htmlContent for call pulses — see
        // server/services/call-pulse.service.ts renderPulseHtml. Inline styles
        // intentionally retained so the same HTML also works in plain-HTML
        // contexts (email forwarding, admin viewer).
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(pulse.htmlContent) }}
      />}

      <Dialog open={!!lightboxSrc} onOpenChange={(o) => { if (!o) setLightboxSrc(null); }}>
        <DialogContent className="max-w-5xl bg-gray-900 border-gray-700 p-2">
          <DialogTitle className="sr-only">{lightboxAlt || 'Snapshot'}</DialogTitle>
          {lightboxSrc && (
            // Re-renders the same proxy URL so the auth cookie / token cycle
            // gates this fetch identically to the thumbnail.
            <img
              src={lightboxSrc}
              alt={lightboxAlt}
              className="w-full h-auto max-h-[85vh] object-contain bg-black"
              data-testid="call-pulse-lightbox-img"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CallPulseCard;
